import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Typography, Space, message, Popconfirm, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, HomeOutlined } from '@ant-design/icons';
import { Warehouse, CreateWarehouseData, UpdateWarehouseData } from '../types.ts';
import { warehouseAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

const { Title } = Typography;
const { Search } = Input;

const Warehouses: React.FC = () => {
  const { canWrite } = useAuth();
  const canEdit = canWrite('warehouses');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState<Warehouse | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [form] = Form.useForm();

  const fetchWarehouses = async (search?: string) => {
    setLoading(true);
    try {
      const data = await warehouseAPI.getWarehouses(search);
      setWarehouses(data);
    } catch (error) {
      message.error('Errore nel caricamento magazzini');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const handleAdd = () => {
    setEditingWarehouse(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (warehouse: Warehouse) => {
    setEditingWarehouse(warehouse);
    form.setFieldsValue({
      cod_mag: warehouse.cod_mag,
      description: warehouse.description,
      address: warehouse.address,
      is_nationalized: warehouse.is_nationalized,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await warehouseAPI.deleteWarehouse(id);
      message.success('Magazzino eliminato con successo');
      fetchWarehouses(searchTerm);
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nell\'eliminazione del magazzino');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingWarehouse) {
        const updateData: UpdateWarehouseData = values;
        await warehouseAPI.updateWarehouse(editingWarehouse.id, updateData);
        message.success('Magazzino aggiornato con successo');
      } else {
        const createData: CreateWarehouseData = values;
        await warehouseAPI.createWarehouse(createData);
        message.success('Magazzino creato con successo');
      }
      setModalVisible(false);
      fetchWarehouses(searchTerm);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Errore nell\'operazione';
      message.error(errorMessage);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    fetchWarehouses(value);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Cod. Mag.',
      dataIndex: 'cod_mag',
      key: 'cod_mag',
      width: 120,
      render: (cod_mag: string) => cod_mag || '-',
    },
    {
      title: 'Descrizione',
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => <strong>{description}</strong>,
    },
    {
      title: 'Indirizzo',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
    },
    {
      title: 'Nazionalizzato',
      dataIndex: 'is_nationalized',
      key: 'is_nationalized',
      render: (isNationalized: boolean) => (
        <span style={{ color: isNationalized ? '#52c41a' : '#ff4d4f' }}>
          {isNationalized ? 'Sì' : 'No'}
        </span>
      ),
    },
    {
      title: 'Data Creazione',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString('it-IT'),
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 120,
      render: (_, record: Warehouse) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Sei sicuro di voler eliminare questo magazzino?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sì"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>
          <HomeOutlined style={{ marginRight: 8 }} />
          Gestione Magazzini
        </Title>
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Nuovo Magazzino
          </Button>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <Search
          placeholder="Cerca per descrizione o indirizzo..."
          allowClear
          enterButton={<SearchOutlined />}
          size="large"
          onSearch={handleSearch}
          style={{ maxWidth: 400 }}
        />
      </div>

      <Table
        columns={canEdit ? columns : columns.filter((c) => c.key !== 'actions')}
        dataSource={warehouses}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingWarehouse ? 'Modifica Magazzino' : 'Nuovo Magazzino'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        destroyOnClose
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="cod_mag"
            label="Codice Magazzino"
            tooltip="Codice identificativo del magazzino (opzionale)"
          >
            <Input placeholder="Inserisci il codice del magazzino" maxLength={50} />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="Descrizione"
            rules={[{ required: true, message: 'Descrizione richiesta' }]}
          >
            <Input placeholder="Inserisci la descrizione del magazzino" />
          </Form.Item>
          
          <Form.Item
            name="address"
            label="Indirizzo"
            rules={[{ required: true, message: 'Indirizzo richiesto' }]}
          >
            <Input.TextArea rows={3} placeholder="Inserisci l'indirizzo completo del magazzino" />
          </Form.Item>
          
          <Form.Item
            name="is_nationalized"
            label="Magazzino Nazionalizzato"
            valuePropName="checked"
            tooltip="Indica se il magazzino è sotto controllo nazionale"
          >
            <Switch 
              checkedChildren="Sì"
              unCheckedChildren="No"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Warehouses;
